import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../auth/authActions";
import { useHistory, useLocation } from "react-router-dom";
import { clone, partial } from "ramda";
import { notBlank } from "../../util/ext";
import { useSearchFilter } from "../../common/use-searchFilter";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import Grid from "@material-ui/core/Grid";
import LuxonUtils from "@date-io/luxon";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";

import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import TotalsHeader from "../../creator-application/common/TotalsHeader";
import MetadataFileDiscoveryTable from "./MetadataFileDiscoveryTable";
import {
  useContentProviderEndpoints,
  useContentProviders,
} from "./processingDiscoveryServices";
import ContentProviderEndpointAutoComplete from "../ContentProviderEndpointAutoComplete";
import RealTimeSearchBar from "../../creator-application/common/RealTimeSearchBar";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const MetadataFileDiscoveryBrowse = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();
  const history = useHistory();
  const location = useLocation();

  const [metadataFileDiscoveries, setMetadataFileDiscoveries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [contentProviders, setContentProviders] = useState([]);
  const allContentProviders = useContentProviders();
  useEffect(() => {
    if (allContentProviders.isSuccess && notBlank(allContentProviders.data)) {
      setContentProviders(allContentProviders.data);
    }
  }, [allContentProviders.isSuccess, allContentProviders.data]);

  const [contentProviderEndpoints, setContentProviderEndpoints] = useState([]);
  const allContentProviderEndpoints = useContentProviderEndpoints();
  useEffect(() => {
    if (
      allContentProviderEndpoints.isSuccess &&
      notBlank(allContentProviderEndpoints.data)
    ) {
      setContentProviderEndpoints(allContentProviderEndpoints.data);
    }
  }, [allContentProviderEndpoints.isSuccess, allContentProviderEndpoints.data]);

  // //-- Handle Search state
  const initialState = clone(searchState);
  const {
    state,
    handleUpdateSort,
    handleUpdatePage,
    handleUpdateFilter,
    handleUpdateSearch,
  } = useSearchFilter(initialState);

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      dispatch(logOut());
    }

    if (searchResults.isSuccess) {
      setScrollVisible(true);
      setMetadataFileDiscoveries(searchResults.data.content);
      setTotalCount(searchResults.data.totalElements);
    }
  }, [
    dispatch,
    auth,
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    searchResults.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  return (
    <Container id="subscription-browse-view" classes={{ root: classes.root }}>
      <div>
        <RealTimeSearchBar
          query={state.appliedFilter}
          onQueryChange={handleUpdateFilter}
          backButton={
            notBlank(location.state) &&
            notBlank(location.state.previousLocation) && (
              <Button
                aria-label="Go back"
                onClick={() => history.push(location.state.previousLocation)}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </Button>
            )
          }
          otherFilters={
            <Grid
              container
              alignItems="center"
              direction="row"
              spacing={2}
              classes={{ root: classes.filters }}
            >
              <Grid item xs={4} sm={2}>
                <MuiPickersUtilsProvider utils={LuxonUtils}>
                  <KeyboardDatePicker
                    // disableToolbar
                    fullWidth
                    autoOk
                    id="startDate"
                    format="MM/dd/yyyy"
                    label="From Date"
                    variant="inline"
                    value={searchState.startDate}
                    onChange={partial(handleUpdateSearch, ["startDate"])}
                    KeyboardButtonProps={{
                      "aria-label": "change start date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={4} sm={2}>
                <MuiPickersUtilsProvider utils={LuxonUtils}>
                  <KeyboardDatePicker
                    // disableToolbar
                    fullWidth
                    autoOk
                    id="endDate"
                    format="MM/dd/yyyy"
                    label="To Date"
                    variant="inline"
                    value={searchState.endDate}
                    onChange={partial(handleUpdateSearch, ["endDate"])}
                    KeyboardButtonProps={{
                      "aria-label": "change end date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={6} sm={6}>
                <ContentProviderEndpointAutoComplete
                  value={state.contentProviderEndpointId}
                  label="Filter By Content Provider Endpoint"
                  onChange={partial(handleUpdateSearch, [
                    "contentProviderEndpointId",
                  ])}
                />
              </Grid>
            </Grid>
          }
        />

        <TotalsHeader countText="Discovery Event" count={totalCount} />

        {searchResults.isLoading && <PanelLoadingIndicator />}
        {searchResults.isSuccess && (
          <Container classes={{ root: classes.panel }}>
            {notBlank(metadataFileDiscoveries) ? (
              <MetadataFileDiscoveryTable
                metadataFileDiscoveryList={metadataFileDiscoveries}
                total={totalCount}
                contentProviderList={contentProviders}
                contentProviderEndpointList={contentProviderEndpoints}
                page={state.page}
                orderBy={state.orderBy}
                order={state.orderDirection}
                onSortChange={handleUpdateSort}
                onPageChange={handleUpdatePage}
                onScrollVisible={handleScrollVisible}
              />
            ) : (
              <Box
                height="calc(100vh - 400px)"
                padding={5}
                fontSize="1.6rem"
                fontStyle="italic"
              >
                No Metadata Discovery Events found matching given criteria.
              </Box>
            )}
          </Container>
        )}
      </div>
    </Container>
  );
};

export default MetadataFileDiscoveryBrowse;
